import React, { forwardRef } from 'react'
import { format } from 'date-fns'
import { TimelineItem } from '../types'
import { TimesSolid } from '@convious/icons'
import { t } from 'translations'
import './capacityItemTooltip.scss'

type CapacityItemTooltipProps = {
    item: TimelineItem
    strategy: 'absolute' | 'fixed'
    x: number | null
    y: number | null
}

const UnlimitedItemTooltipContent = (item: TimelineItem) => (
    <>
        <strong>{t('orders.capacityReport.tooltip.reserved', { reserved: item.reserved ?? '' })}</strong>
        <span>
            <TimesSolid /> {t('orders.capacityReport.unlimited')}
        </span>
        <span>{t('orders.capacityReport.tooltip.from', { time: format(item.start, 'HH:mm') })}</span>
        <span>{t('orders.capacityReport.tooltip.to', { time: format(item.end, 'HH:mm') })}</span>
    </>
)

const ClosedItemTooltipContent = (item: TimelineItem) => (
    <>
        <span>
            <TimesSolid /> {t('orders.capacityReport.closed')}
        </span>
        <span>{t('orders.capacityReport.tooltip.from', { time: format(item.start, 'HH:mm') })}</span>
        <span>{t('orders.capacityReport.tooltip.to', { time: format(item.end, 'HH:mm') })}</span>
    </>
)

const PeriodItemTooltipContent = (item: TimelineItem) => (
    <>
        <strong>{t('orders.capacityReport.tooltip.reserved', { reserved: item.reserved ?? '' })}</strong>
        <span>{t('orders.capacityReport.tooltip.remaining', { available: item.available ?? '' })}</span>
        <span>{t('orders.capacityReport.tooltip.total', { capacity: item.capacity ?? '' })}</span>
        <span>{t('orders.capacityReport.tooltip.from', { time: format(item.start, 'HH:mm') })}</span>
        <span>{t('orders.capacityReport.tooltip.to', { time: format(item.end, 'HH:mm') })}</span>
    </>
)

const InstantItemTooltipContent = (item: TimelineItem) => (
    <>
        <strong>{t('orders.capacityReport.tooltip.reserved', { reserved: item.reserved ?? '' })}</strong>
        <span>{t('orders.capacityReport.tooltip.remaining', { available: item.available ?? '' })}</span>
        <span>{t('orders.capacityReport.tooltip.total', { capacity: item.capacity ?? '' })}</span>
        <span>{t('orders.capacityReport.tooltip.time', { time: format(item.start, 'HH:mm') })}</span>
    </>
)

const CapacityItemTooltip = forwardRef<HTMLDivElement, CapacityItemTooltipProps>(({ strategy, item, x, y }, ref) => {
    const getTooltipContent = () => {
        if (item.capacity === 0) {
            return ClosedItemTooltipContent(item)
        }
        if (item.capacity === null) {
            return UnlimitedItemTooltipContent(item)
        }
        if (item.reservationType === 'instant') {
            return InstantItemTooltipContent(item)
        }
        return PeriodItemTooltipContent(item)
    }

    return (
        <div className="capacity-item-tooltip" ref={ref} style={{ top: y ?? 0, left: x ?? 0, position: strategy }}>
            {getTooltipContent()}
        </div>
    )
})

CapacityItemTooltip.displayName = 'Tooltip'

export default CapacityItemTooltip
